import React, { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Download, FileText, Package, User, Building, AlertTriangle, Folder } from 'lucide-react';
import { toast } from 'sonner';

const OWNER_TYPES = [
  { value: 'PARTICIPANT', label: 'Participant', icon: User },
  { value: 'STAFF', label: 'Staff', icon: User },
  { value: 'INCIDENT', label: 'Incident', icon: AlertTriangle },
  { value: 'HOUSE', label: 'House', icon: Building },
];

const CATEGORIES = ['ID', 'SERVICE_AGREEMENT', 'CONSENT', 'POLICY', 'HR', 'INCIDENT_DOC', 'OTHER'];

export default function DocumentExportPage() {
  const [ownerType, setOwnerType] = useState('');
  const [ownerId, setOwnerId] = useState('');
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [owners, setOwners] = useState([]);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    if (ownerType === 'PARTICIPANT') {
      api.get('/participants?pageSize=100').then(r => setOwners(r.data.items?.map(p => ({ id: p.id, name: `${p.first_name} ${p.last_name} (${p.ndis_number || 'N/A'})` })) || []));
    } else if (ownerType === 'STAFF') {
      api.get('/staff?pageSize=100').then(r => setOwners(r.data.items?.map(s => ({ id: s.id, name: s.user_full_name || s.full_name || 'Staff' })) || []));
    } else if (ownerType === 'HOUSE') {
      api.get('/houses?pageSize=100').then(r => setOwners(r.data.items?.map(h => ({ id: h.id, name: h.name })) || [])).catch(() => setOwners([]));
    } else if (ownerType === 'INCIDENT') {
      api.get('/incidents?pageSize=100').then(r => setOwners(r.data.items?.map(i => ({ id: i.id, name: `${i.incident_type} - ${i.title || i.created_at?.slice(0, 10)}` })) || [])).catch(() => setOwners([]));
    } else {
      setOwners([]);
    }
    setOwnerId('');
    setPreview(null);
  }, [ownerType]);

  const handlePreview = async () => {
    if (!ownerType || !ownerId) {
      toast.error('Please select owner type and owner');
      return;
    }
    setLoading(true);
    try {
      const res = await api.post('/documents/export-pack/preview', {
        ownerType,
        ownerId,
        categories: selectedCategories.length > 0 ? selectedCategories : undefined,
        includeArchived
      });
      setPreview(res.data.data);
    } catch (err) {
      toast.error(err.response?.data?.error?.message || 'Failed to preview export');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    if (!ownerType || !ownerId) {
      toast.error('Please select owner type and owner');
      return;
    }
    setDownloading(true);
    try {
      const res = await api.post('/documents/export-pack', {
        ownerType,
        ownerId,
        categories: selectedCategories.length > 0 ? selectedCategories : undefined,
        includeArchived
      }, { responseType: 'blob' });
      
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      const ownerName = owners.find(o => o.id === ownerId)?.name || 'export';
      link.download = `${ownerType.toLowerCase()}_${ownerName.replace(/\s+/g, '_')}_documents.zip`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success('Document pack downloaded');
    } catch (err) {
      toast.error(err.response?.data?.error?.message || 'Failed to download export');
    } finally {
      setDownloading(false);
    }
  };

  const toggleCategory = (cat) => {
    setSelectedCategories(prev => prev.includes(cat) ? prev.filter(c => c !== cat) : [...prev, cat]);
  };

  return (
    <div className="space-y-6" data-testid="document-export-page">
      <div>
        <h1 className="text-2xl font-bold">Document Pack Export</h1>
        <p className="text-muted-foreground">Download all documents for a participant, staff member, or incident</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Package className="h-5 w-5" />
              Export Configuration
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Owner Type *</Label>
              <div className="grid grid-cols-2 gap-2 mt-2">
                {OWNER_TYPES.map(type => {
                  const Icon = type.icon;
                  return (
                    <button
                      key={type.value}
                      type="button"
                      onClick={() => setOwnerType(type.value)}
                      className={`flex items-center gap-2 p-3 rounded-lg border transition-colors ${
                        ownerType === type.value ? 'border-primary bg-primary/5' : 'hover:bg-muted'
                      }`}
                      data-testid={`owner-type-${type.value}`}
                    >
                      <Icon className="h-5 w-5" />
                      <span className="font-medium">{type.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <Label>Select {ownerType || 'Owner'} *</Label>
              <Select value={ownerId} onValueChange={setOwnerId} disabled={!ownerType || owners.length === 0}>
                <SelectTrigger data-testid="owner-select"><SelectValue placeholder={`Select ${ownerType?.toLowerCase() || 'owner'}...`} /></SelectTrigger>
                <SelectContent>
                  {owners.map(o => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Filter by Category (optional)</Label>
              <div className="grid grid-cols-2 gap-2 mt-2">
                {CATEGORIES.map(cat => (
                  <label key={cat} className="flex items-center gap-2 p-2 rounded border hover:bg-muted cursor-pointer">
                    <Checkbox
                      checked={selectedCategories.includes(cat)}
                      onCheckedChange={() => toggleCategory(cat)}
                    />
                    <span className="text-sm">{cat.replace('_', ' ')}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Checkbox
                checked={includeArchived}
                onCheckedChange={setIncludeArchived}
                id="includeArchived"
              />
              <label htmlFor="includeArchived" className="text-sm cursor-pointer">Include archived documents</label>
            </div>

            <div className="flex gap-2 pt-4">
              <Button variant="outline" onClick={handlePreview} disabled={loading || !ownerType || !ownerId} className="flex-1" data-testid="preview-btn">
                {loading ? 'Loading...' : 'Preview Export'}
              </Button>
              <Button onClick={handleDownload} disabled={downloading || !ownerType || !ownerId} className="flex-1" data-testid="download-btn">
                <Download className="h-4 w-4 mr-2" />
                {downloading ? 'Downloading...' : 'Download ZIP'}
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Export Preview
            </CardTitle>
          </CardHeader>
          <CardContent>
            {!preview ? (
              <div className="text-center py-12 text-muted-foreground">
                <Package className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>Select owner and click "Preview Export" to see what will be included</p>
              </div>
            ) : preview.totalDocuments === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <AlertTriangle className="h-12 w-12 mx-auto mb-3 text-orange-500" />
                <p>No documents found matching the criteria</p>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4 p-4 bg-muted rounded-lg">
                  <div>
                    <p className="text-sm text-muted-foreground">Total Documents</p>
                    <p className="text-2xl font-bold">{preview.totalDocuments}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Total Size</p>
                    <p className="text-2xl font-bold">{preview.totalSizeFormatted}</p>
                  </div>
                </div>

                <div>
                  <p className="text-sm font-medium mb-2">Documents by Category</p>
                  <div className="space-y-2">
                    {Object.entries(preview.byCategory || {}).map(([category, docs]) => (
                      <div key={category} className="p-3 border rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <Folder className="h-4 w-4 text-muted-foreground" />
                            <span className="font-medium">{category}</span>
                          </div>
                          <span className="text-sm text-muted-foreground">{docs.length} files</span>
                        </div>
                        <div className="space-y-1">
                          {docs.map(doc => (
                            <div key={doc.id} className="flex items-center gap-2 text-sm text-muted-foreground">
                              <FileText className="h-3.5 w-3.5" />
                              <span className="truncate">{doc.title}</span>
                              {!doc.hasFile && <AlertTriangle className="h-3.5 w-3.5 text-orange-500" />}
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
